import { useEffect, useMemo, useState } from 'react';

import { InfoTooltip } from '../../Common/InfoTooltip';
import type { Chat, SystemStatus } from '../../../types';

type Props = {
  enabled: boolean;
  onToggle: (enabled: boolean) => Promise<void>;
  activeChat: Chat | null;
  systemStatus: SystemStatus | null;
};

const messagingWindowMs = 24 * 60 * 60 * 1_000;

type Condition = { key: string; label: string; ok: boolean | null; info: string; block: string };

const AutoReplyPanel = ({ enabled, onToggle, activeChat, systemStatus }: Props) => {
  const [toggling, setToggling] = useState(false);
  const [now, setNow] = useState(Date.now);

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(id);
  }, []);

  const handleToggle = async () => {
    setToggling(true);
    try {
      await onToggle(!enabled);
    } catch {
      // error is shown in the global banner
    } finally {
      setToggling(false);
    }
  };

  const conditions: Condition[] = useMemo(() => {
    const lastInbound = activeChat?.lastInboundAt ? Date.parse(activeChat.lastInboundAt) : 0;
    const windowOpen = Boolean(
      activeChat && lastInbound && now - lastInbound <= messagingWindowMs && now >= lastInbound,
    );
    const paused = Boolean(
      activeChat?.pausedUntil && Date.parse(activeChat.pausedUntil) > now,
    );

    return [
      {
        key: 'global',
        label: 'Globale automode',
        ok: enabled,
        info: 'Hoofdschakelaar op Instellingen. Uit = geen automatische replies voor niemand.',
        block: 'Globale automode staat uit. Schakel dit in via Instellingen of deze knop.',
      },
      {
        key: 'chat',
        label: 'Per-chat automode',
        ok: activeChat ? activeChat.automode === 'on' || activeChat.automode === 'inherit' : null,
        info: "'Inherit' volgt de globale schakelaar; 'Aan' dwingt aan; 'Uit' blokkeert deze chat.",
        block: 'Per-chat automode staat op Uit voor dit gesprek.',
      },
      {
        key: 'consent',
        label: 'Toestemming',
        ok: activeChat ? activeChat.consent === 'opted_in' : null,
        info: 'De contactpersoon moet expliciet toestemming hebben gegeven (opted_in).',
        block: 'Ontvanger heeft geen toestemming gegeven of is uitgeschreven.',
      },
      {
        key: 'window',
        label: '24-uurs venster',
        ok: activeChat ? windowOpen : null,
        info: 'Na het laatste inkomende bericht blijft het antwoordvenster 24 uur open.',
        block: '24-uurs berichtenvenster is gesloten sinds de laatste inbound.',
      },
      {
        key: 'pause',
        label: 'Niet gepauzeerd',
        ok: activeChat ? !paused : null,
        info: 'Een handmatige pauze onderbreekt automatische replies in dit gesprek.',
        block: 'Dit gesprek is gepauzeerd tot later.',
      },
      {
        key: 'connector',
        label: 'WhatsApp verbinding',
        ok: systemStatus?.whatsapp.state === 'ready',
        info: 'WhatsApp-connector moet verbonden en klaar zijn om berichten te versturen.',
        block: `WhatsApp is niet klaar: ${systemStatus?.whatsapp.reason ?? systemStatus?.whatsapp.state ?? 'onbekend'}.`,
      },
      {
        key: 'ai',
        label: 'AI provider klaar',
        ok: systemStatus?.ai?.state === 'ready',
        info: 'De geconfigureerde AI-provider moet bereikbaar en getest zijn.',
        block: `AI provider is niet klaar: ${systemStatus?.ai?.reason ?? systemStatus?.ai?.state ?? 'onbekend'}.`,
      },
    ];
  }, [enabled, activeChat, systemStatus, now]);

  const firstBlocker = conditions.find((c) => c.ok === false);
  const overallOk = conditions.every((c) => c.ok === true);

  return (
    <div className="autoreply-panel">
      <p className="tool-description">
        Wanneer Autoreply BOT aan staat, genereert de AI automatisch replies voor binnenkomende
        berichten — maar alleen als alle onderstaande voorwaarden groen zijn.
      </p>
      <div className="autoreply-status" role="status" aria-live="polite">
        {overallOk ? (
          <span className="status-indicator running">
            ● Automatisch antwoorden: AAN
          </span>
        ) : firstBlocker ? (
          <span className="status-indicator stopped">
            ● Automatisch antwoorden: UIT — {firstBlocker.block}
          </span>
        ) : (
          <span className="status-indicator stopped">
            ● Automatisch antwoorden: UIT
          </span>
        )}
      </div>
      <button
        type="button"
        className={`button ${enabled ? 'danger' : 'primary'} small`}
        disabled={toggling}
        onClick={handleToggle}
      >
        {toggling ? 'Bezig…' : enabled ? 'Stoppen' : 'Starten'}
      </button>
      <div className="autoreply-info">
        <h4>Vereisten voor automatische AI-reply</h4>
        <ul>
          {conditions.map((condition) => (
            <li key={condition.key} className={condition.ok === true ? 'ok' : condition.ok === false ? 'not-ok' : 'unknown'}>
              {condition.ok === true ? '✓' : condition.ok === false ? '✗' : '○'} {condition.label}
              {' '}
              <InfoTooltip text={condition.info} />
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default AutoReplyPanel;
